import type { CommandAuditEntry, CommandEvent } from "@ceo-cmd/shared";

function eventLabel(event: CommandEvent): string {
  switch (event.type) {
    case "ItemCreated":
      return "Created";
    case "ItemIngested":
      return "Ingested";
    case "EvidenceAppended":
      return "Evidence";
    case "DispositionApplied": {
      const d = (
        event.payload as { type: "DispositionApplied"; disposition: string }
      ).disposition;
      return d.replace(/_/g, " ");
    }
    case "DispositionUndone":
      return "Undone";
    case "TimerElapsed":
      return "Timer";
    case "ExternalWriteRecorded":
      return "External";
    default:
      return event.type;
  }
}

function eventDetail(event: CommandEvent): string {
  const p = event.payload;
  switch (p.type) {
    case "ItemCreated":
    case "ItemIngested":
      return p.item.title;
    case "EvidenceAppended":
      return p.signal.label;
    case "DispositionApplied":
      return p.message;
    case "DispositionUndone":
      return p.message;
    case "TimerElapsed":
      return p.message;
    case "ExternalWriteRecorded":
      return p.write.message;
    default:
      return "";
  }
}

export function DecisionHistory({
  events,
  auditFallback,
}: {
  events?: CommandEvent[];
  auditFallback?: CommandAuditEntry[];
}) {
  if (events && events.length > 0) {
    return (
      <div>
        <p className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
          Decision history
        </p>
        <ul className="space-y-1 text-xs text-muted-foreground">
          {[...events]
            .sort((a, b) => a.seq - b.seq)
            .map((event) => (
              <li
                key={event.eventId}
                className="rounded border border-border px-2 py-1.5"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium capitalize text-foreground">
                    {eventLabel(event)}
                  </span>
                  <span className="shrink-0 tabular-nums text-[10px]">
                    {new Date(event.at).toLocaleString()}
                  </span>
                </div>
                <p className="mt-0.5 text-foreground/90">{eventDetail(event)}</p>
                {event.type === "ExternalWriteRecorded" && (
                  <p className="mt-0.5 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400">
                    Simulated integration
                  </p>
                )}
              </li>
            ))}
        </ul>
      </div>
    );
  }

  if (auditFallback && auditFallback.length > 0) {
    return (
      <div>
        <p className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
          Decision history
        </p>
        <ul className="space-y-1 text-xs text-muted-foreground">
          {auditFallback.map((a) => (
            <li key={a.id} className="rounded border border-border px-2 py-1">
              <span className="font-medium text-foreground">{a.event}</span>
              {" — "}
              {a.detail}
              {a.sideEffects && a.sideEffects.length > 0 && (
                <p className="mt-0.5 text-[10px] text-amber-700 dark:text-amber-400">
                  {a.sideEffects.join(" · ")}
                </p>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <p className="text-xs text-muted-foreground">No decisions recorded yet.</p>
  );
}
